import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { useCompany } from "@/context/CompanyContext";

const money = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(v || 0);

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("PENDING");
  const navigate = useNavigate();
  const { company } = useCompany();

  useEffect(() => {
    api.get("/bookings", { params: { payment_status: tab, limit: 2000 } }).then((r) => setRows(r.data));
  }, [tab, company]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.count += 1;
      acc.payout += Number(r.host_payout || 0);
      acc.deposit += Number(r.security_deposit || 0);
      return acc;
    },
    { count: 0, payout: 0, deposit: 0 }
  );

  const tabs = [
    { key: "PENDING", label: "Pending" },
    { key: "PAID", label: "Paid" },
    { key: "REFUNDED", label: "Refunded" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 lg:space-y-8 max-w-[1400px]">
      <div>
        <div className="label-eyebrow">Cash flow</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">
          Payment Tracker
        </h1>
        <p className="text-sm text-slate-500 mt-2">Monitor host payouts and outstanding balances.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Records" value={totals.count} testid="pay-count" />
        <Stat label="Total Payout" value={money(totals.payout)} testid="pay-payout" />
        <Stat label="Total Deposits" value={money(totals.deposit)} testid="pay-deposit" />
      </div>

      <div className="flex gap-2 border-b border-zinc-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`payment-tab-${t.key.toLowerCase()}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No {tab.toLowerCase()} payments.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Reservation</th>
                <th className="text-left px-4 py-3 font-semibold">Property</th>
                <th className="text-left px-4 py-3 font-semibold">Guest</th>
                <th className="text-left px-4 py-3 font-semibold">Check-in</th>
                <th className="text-right px-4 py-3 font-semibold">Payout</th>
                <th className="text-right px-4 py-3 font-semibold">Deposit</th>
                <th className="text-left px-4 py-3 font-semibold">Date Paid</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/bookings/${r.id}`)}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors"
                  data-testid={`payment-row-${r.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs">{r.reservation_code || "—"}</td>
                  <td className="px-4 py-3 font-medium">{r.property_booked || "—"}</td>
                  <td className="px-4 py-3">{r.booked_by || "—"}</td>
                  <td className="px-4 py-3">{r.check_in || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{money(r.host_payout)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(r.security_deposit)}</td>
                  <td className="px-4 py-3">{r.date_paid || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge value={r.payment_status} type="payment" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-heading text-3xl font-semibold tracking-tight mt-3">{value}</div>
    </div>
  );
}
